import { each, has, get } from 'lodash'

/**
 * A mixin that injects a few methods handy for handling/displaying errors.
 * Note that this mixin will only work with the Vuetify UX.
 */
const ErrorMixin = {
  methods: {
    /**
     * Wrapper method to handle a generic error, delegating to the other error handler methods as appropriate.
     * This method also rethrows the error so it is logged in Kibana.
     * @param error
     */
    handleError (error) {
      // If the vuex store isn't available for some reason (maybe not available yet or on a component that doesn't have
      // access to it, then just throw the error, which will abort further processing below.
      if (this.$store == null) {
        throw (error)
      }
      // If this component has already been torn down then the user navigated away before the async request that
      // failed had settled, and the failure is no longer relevant to what is on screen. Rendering a modal here pops
      // an error over whichever page they moved to - e.g. an abandoned RouteView fetch raising the generic modal on
      // /routes. Rethrow so the error is still logged, but show nothing. (VF-4321)
      if (this.isComponentDestroyed()) {
        throw (error)
      }
      // If it has a response field, then it's an API error.
      if (has(error, 'response')) {
        this.handleNetworkError(error)
        // Otherwise, handle as a generic error
      } else {
        this.handleGenericError(error)
      }
      // Rethrow the error so we log it.
      throw (error)
    },
    /**
     * Show a generic error message - typically results from non-networking code bugs that throw an error and bubble out
     * into a catch handler in a promise somewhere.
     * @param error
     */
    handleGenericError (error) {
      this.$store.dispatch('globalMessagingModule/showModal', {
        message: this.safeTranslate(
          'generic_error_message',
          'There appears to have been an error while trying to process your request. Please try again or contact support.'
        )
      })
    },
    /**
     * Translate a key, falling back to a literal English string if $t() throws.
     * vue-i18n's $t() can raise "Cannot read properties of null (reading '_t')"
     * when the catalog isn't initialised in this component's context (seen around
     * session/token expiry). Because the error handlers below call $t(), that
     * turned a recoverable error into a hard crash *inside the handler* and
     * masked the original error from our logs. Guarding here keeps the modal
     * rendering and lets the real error surface via handleError()'s rethrow. (VF-4109)
     * @param key i18n key
     * @param fallback literal string to show if translation is unavailable
     */
    safeTranslate (key, fallback) {
      try {
        return this.$t(key)
      } catch (e) {
        return fallback
      }
    },
    /**
     * Whether Vue has torn down (or is tearing down) this component instance.
     * Async work started by a component outlives the component when the user navigates away mid-request, and a
     * destroyed instance is no longer safe to touch - vue-i18n's beforeDestroy hook nulls out $i18n, so $t()/$tc()
     * on one throws "Cannot read properties of null (reading '_t'/'_tc')".
     * @returns {boolean}
     */
    isComponentDestroyed () {
      return this._isDestroyed === true || this._isBeingDestroyed === true
    },
    /**
     * Handles networking errors.
     * We are deliberately choosing to show a generic networking error message here rather than show any potentially
     * confusing messages that result from an exception. Generally if the user sees these we either have a bug or
     * there is a networking issue.
     * @param error
     */
    handleNetworkError (error) {
      // If a 404, show a special 404 error banner (since it may be related to user permissions)
      if (get(error, 'response.status') === 404) {
        this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', this.safeTranslate('404_error_message', 'Your account does not have access to this resource. If you believe this is a mistake please contact support.'))
        // If response.data.errors exists, it must be a record save error.
      } else if (has(error, 'response.data.errors')) {
        this.handleRecordSaveError(error)
      } else {
        // Otherwise just show a generic network error message - no need to display any internal messages
        this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.safeTranslate('network_error_message', 'There was an issue with the request. Please try again or contact support.')
        })
      }
    },
    /**
     * Handles errors thrown when a record fails to save. This is triggered when error.response.data.errors exists.
     * In a perfect world we should never see these because client-side validations should have prevented any possible
     * bad states in the entity, but just in case, we format the errors into a list and display a banner.
     * @param error
     */
    handleRecordSaveError (error) {
      let output = ''
      const message = get(error, 'response.data.message', this.safeTranslate('error', 'Error'))
      const errorMessages = get(error, 'response.data.errors')
      if (errorMessages) {
        output += message + '<br>'
        output += '<ul class="my-0">'
        each(errorMessages, field => {
          each(field, errorString => {
            output += `<li>${errorString}</li>`
          })
        })
        output += '</ul>'
        // Clear existing flash messages then show the banner
        this.$store.dispatch('globalMessagingModule/clearFlashBanners').then(() => {
          this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', output)
        })
      }
    }
  }
}

export default ErrorMixin
